import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiChevronUp, FiChevronDown, FiX } from 'react-icons/fi';

/**
 * 편집기 툴바의 검색 창 — SQL 편집 툴바(App 의 renderEditorToolbar)와 **같은 모양·클래스**
 * (`editor-search-bar` · `editor-search-input` · `detach-inline-btn`, 스타일은 DetachablePanel.css · App.css).
 *
 * 검색은 대소문자 무시 · 일치 위치로 선택을 옮긴다(App.handleEditorSearch 와 같은 규칙).
 * 편집기에는 SQLEditor 의 `onFind`(Ctrl+F) · `onFindNav`(F3 · Ctrl+G)로 이 훅의 `find`·`nav` 를 넘긴다.
 *
 * 처음 쓰는 곳은 PL/SQL 편집기(2026-11-02). App 은 아직 같은 마크업을 직접 그린다 — 옮길 때 이것을 쓸 것.
 */
export function useEditorSearch(viewRef) {
    const [state, setState] = useState({ open: true, text: '', matchIndex: -1, matchCount: 0 });
    const inputRef = useRef(null);

    const positionsOf = (doc, text) => {
        const upper = doc.toUpperCase();
        const q = text.toUpperCase();
        const out = [];
        let idx = 0;
        while (q && (idx = upper.indexOf(q, idx)) !== -1) { out.push(idx); idx += q.length; }
        return out;
    };

    const search = useCallback((text) => {
        const view = viewRef.current;
        if (!view || !text) {
            setState(p => ({ ...p, text, matchIndex: -1, matchCount: 0 }));
            return;
        }
        const pos = positionsOf(view.state.doc.toString(), text);
        setState(p => ({ ...p, text, matchCount: pos.length, matchIndex: pos.length > 0 ? 0 : -1 }));
        if (pos.length > 0) view.dispatch({ selection: { anchor: pos[0], head: pos[0] + text.length }, scrollIntoView: true });
    }, [viewRef]);

    const nav = useCallback((direction) => {
        const view = viewRef.current;
        if (!view || !state.text) return;
        const pos = positionsOf(view.state.doc.toString(), state.text);
        if (pos.length === 0) return;
        const next = direction === 'next'
            ? (state.matchIndex + 1) % pos.length
            : (state.matchIndex - 1 + pos.length) % pos.length;
        setState(p => ({ ...p, matchIndex: next, matchCount: pos.length }));
        view.dispatch({ selection: { anchor: pos[next], head: pos[next] + state.text.length }, scrollIntoView: true });
    }, [viewRef, state.text, state.matchIndex]);

    // 편집기 Ctrl+F — 선택한 글자가 있으면 검색어로 쓰고 검색 칸에 포커스
    const find = useCallback((selectedText) => {
        setState(p => ({ ...p, open: true }));
        if (typeof selectedText === 'string' && selectedText) search(selectedText);
        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) { el.focus(); el.select(); }
        });
    }, [search]);

    const close = useCallback(() => setState(p => ({ ...p, open: false, text: '', matchIndex: -1, matchCount: 0 })), []);

    return { ...state, inputRef, search, nav, find, close };
}

export default function EditorSearchBar({ search }) {
    const { t } = useTranslation();
    if (!search.open) {
        return (
            <button type="button" className="detach-inline-btn" onClick={() => search.find()} title={t('common:ui.searchCtrlF')}>
                <FiSearch /> {t('common:tb.search')}
            </button>
        );
    }
    return (
        <div className="editor-search-bar">
            <FiSearch size={12} />
            <input
                className="editor-search-input"
                type="text"
                value={search.text}
                ref={search.inputRef}
                onChange={(e) => search.search(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') search.nav(e.shiftKey ? 'prev' : 'next');
                    // 창(AppModal) 안에서도 Esc 는 검색만 닫는다 — 창까지 닫히면 편집 중인 화면이 사라진다
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); search.close(); }
                    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') { e.preventDefault(); e.target.select(); }
                }}
                placeholder={t('common:ui.searchPlaceholder')}
            />
            {search.text && <span className="editor-search-count">{search.matchCount > 0 ? `${search.matchIndex + 1}/${search.matchCount}` : '0'}</span>}
            <button type="button" className="editor-search-nav" onClick={() => search.nav('prev')} title={t('common:ui.prev')}><FiChevronUp size={12} /></button>
            <button type="button" className="editor-search-nav" onClick={() => search.nav('next')} title={t('common:ui.next')}><FiChevronDown size={12} /></button>
            <button type="button" className="editor-search-nav" onClick={search.close} title={t('common:ui.closeEsc')}><FiX size={12} /></button>
        </div>
    );
}
